<template>
  <div class="rights-concepts">
    <v-container>
      <v-row justify="center">
        <v-col class="d-flex justify-center">
          <v-btn color="success" @click="multipleConceptsDialog = true">
            <v-icon left>mdi-plus</v-icon>
            <span>Manage multiple</span>
          </v-btn>
        </v-col>
      </v-row>
      <RightsConceptsTab
        :organizationConcepts="organizationConcepts"
        :concepts="concepts"
        :organizationConceptsLoading="organizationConceptsLoading"
        :conceptsLoading="conceptsLoading"
        v-on:openConcept="openConcept"
      />
    </v-container>
    <v-dialog v-model="conceptDialog" max-width="900">
      <RightUsersDialog
        :title="title"
        :admins="selectedConceptUsers.admins"
        :editors="selectedConceptUsers.editors"
        :viewers="selectedConceptUsers.viewers"
        :organizationUsers="organizationUsers"
        :isLoading="organizationUsersLoading"
        v-on:cancel="conceptDialog = false"
        v-on:addAdminUser="addAdminUser"
        v-on:addEditorUser="addEditorUser"
        v-on:addViewerUser="addViewerUser"
        v-on:removeOneAdmin="removeAdminUser"
        v-on:removeOneEditor="removeEditorUser"
        v-on:removeOneViewer="removeViewerUser"
      />
    </v-dialog>
    <v-dialog v-model="multipleConceptsDialog" max-width="900">
      <MultipleConceptsRights
        title="Manage users"
        :organizationUsers="organizationUsers"
        :concepts="concepts"
        v-on:add="addMultiple"
        v-on:remove="removeMultiple"
      />
    </v-dialog>
    <v-snackbar v-model="snackbar" top>
      {{ errorMessage }}
      <v-btn color="red" text @click="snackbar = false">Close</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import mixin from "@/mixins";
import RightsConceptsTab from "@/components/Access/Rights/RightsConceptsTab";
import RightUsersDialog from "@/components/Access/Rights/RightUsersDialog";
import MultipleConceptsRights from "@/components/Access/Rights/MultipleConceptsRights";

export default {
  components: { RightsConceptsTab, RightUsersDialog, MultipleConceptsRights },
  mixins: [mixin],
  data() {
    return {
      title: "",
      concepts: [],
      errorMessage: "",
      snackbar: false,
      organizationConcepts: [],
      organizationUuid: "",
      conceptDialog: false,
      multipleConceptsDialog: false,
      selectedConcept: {},
      selectedConceptUsers: { admins: [], editors: [], viewers: [] },
    };
  },
  computed: {
    ...mapState({
      currentOrganization: (state) => state.organizations.current,
      organizationUsers: (state) => state.organizations.users,
      organizationUsersLoading: (state) =>
        state.organizations.fetchAllUsers.pending,
      conceptsError: (state) => state.concepts.error,
      organizationConceptsLoading: (state) =>
        state.organizations.fetchAllConcepts.pending,
      conceptsLoading: (state) => state.concepts.fetchAll.pending,
    }),
  },
  watch: {
    currentOrganization() {
      this.refresh();
    },
  },
  methods: {
    ...mapActions("organizations", {
      fetchAllOrganizationConcepts: "fetchAllConcepts",
      fetchAllUsers: "fetchAllUsers",
      fetchOrganizationConceptUsers: "fetchOrganizationConceptUsers",
      addOrganizationConceptUser: "addOrganizationConceptUser",
      removeOrganizationConceptUser: "removeOrganizationConceptUser",
    }),
    ...mapActions("concepts", { fetchAllConcepts: "fetchAll" }),
    refresh() {
      this.fetchAllConcepts()
        .then((concepts) => {
          this.concepts = concepts;
        })
        .catch((error) => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
      this.fetchAllOrganizationConcepts({
        organizationUuid: this.currentOrganization.uuid,
      })
        .then((concepts) => {
          this.organizationConcepts = concepts;
        })
        .catch((error) => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
      this.fetchAllUsers({
        organizationUuid: this.currentOrganization.uuid,
      }).catch((error) => {
        this.snackbar = true;
        this.errorMessage = this.errorDisplay(error);
      });
    },
    openConcept(concept) {
      this.fetchOrganizationConceptUsers({
        organizationUuid: this.currentOrganization.uuid,
        conceptUuid: concept.uuid,
      })
        .then((users) => {
          this.title =
            "Users: Concept " +
            this.concepts.find((loc) => loc.uuid === concept.uuid).label;
          this.selectedConceptUsers = users;
          this.selectedConcept = concept;
          this.conceptDialog = true;
        })
        .catch((error) => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
    },
    refreshSelectedConcept() {
      this.fetchOrganizationConceptUsers({
        organizationUuid: this.currentOrganization.uuid,
        conceptUuid: this.selectedConcept.uuid,
      })
        .then((users) => {
          this.selectedConceptUsers = users;
        })
        .catch((error) => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
    },
    addUser(userUuid, role) {
      this.addOrganizationConceptUser({
        organizationUuid: this.currentOrganization.uuid,
        conceptUuid: this.selectedConcept.uuid,
        userUuid: userUuid,
        role: role,
      })
        .then(() => {
          this.refreshSelectedConcept();
        })
        .catch((error) => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
    },
    removeUser(userUuid, role) {
      this.removeOrganizationConceptUser({
        organizationUuid: this.currentOrganization.uuid,
        conceptUuid: this.selectedConcept.uuid,
        userUuid: userUuid,
        role: role,
      })
        .then(() => {
          this.refreshSelectedConcept();
        })
        .catch((error) => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
    },
    addAdminUser(user) {
      this.addUser(user.uuid, "admins");
    },
    addEditorUser(user) {
      this.addUser(user.uuid, "editors");
    },
    addViewerUser(user) {
      this.addUser(user.uuid, "viewers");
    },
    removeAdminUser(user) {
      this.removeUser(user.uuid, "admins");
    },
    removeEditorUser(user) {
      this.removeUser(user.uuid, "editors");
    },
    removeViewerUser(user) {
      this.removeUser(user.uuid, "viewers");
    },
    addMultiple(users, concepts, rights) {
      let promisesData = this.getPromisesData(users, concepts, rights);
      let promises = [];
      for (let data of promisesData) {
        promises.push(this.addOrganizationConceptUser(data));
      }
      Promise.all(promises).then(() => {
        this.refresh();
      });
    },
    removeMultiple(users, concepts, rights) {
      let promisesData = this.getPromisesData(users, concepts, rights);
      let promises = [];
      for (let data of promisesData) {
        promises.push(this.removeOrganizationConceptUser(data));
      }
      Promise.all(promises).then(() => {
        this.refresh();
      });
    },
    getPromisesData(users, concepts, rights) {
      let promisesData = [];
      for (let user of users) {
        for (let concept of concepts) {
          for (let right of rights) {
            promisesData.push({
              organizationUuid: this.currentOrganization.uuid,
              conceptUuid: concept.uuid,
              userUuid: user.uuid,
              role: right.value,
            });
          }
        }
      }
      return promisesData;
    },
  },
  created() {
    if (this.currentOrganization) {
      this.refresh();
    }
  },
};
</script>

<style>
</style>